/**
 * useVorstandsDashboard
 *
 * Lädt das aggregierte View-Model des Vorstands-Dashboards über
 * `GET /api/dashboard/vorstand?period=…` — ein Request statt zehn.
 *
 * Bewusst KEIN Auto-Polling: geladen wird beim Mount und bei Zeitraum-Wechsel
 * sowie über refresh().
 */
import { useCallback, useEffect, useState } from 'react';
import apiClient from '../utils/apiClient';

export type Period = 'monat' | 'quartal' | 'jahr';

export type RoleKey = 'vorstand' | 'trainer' | 'finanzpruefer' | 'mitglied' | 'gast';

/** Status eines Hunde-Pflichtdokuments. */
export type DocStatus = 'valid' | 'soon' | 'overdue' | 'pending' | 'missing';

/** Semantischer Kontotyp (Icon-Zuordnung passiert im Frontend). */
export type AccountIconKey = 'bank' | 'cash' | 'drinks' | 'guestCards' | 'savings';

export interface DashboardKpi {
  activeMembers: number;
  newMembers: number;
  leftMembers: number;
  growthPercent: number;
  guestBookings: number;
  guestBookingsPrevious: number;
  newGuests: number;
  events: number;
  bookings: number;
  capacity: number;
  avgBookings: number;
  utilization: number;
  totalBalance: number;
  accountCount: number;
  reserves: number;
  /** Summe der Einnahmen bzw. Ausgaben im gewählten Zeitraum. */
  cashIn: number;
  cashOut: number;
}

export interface DashboardEvent {
  id: number;
  title: string;
  start: string;
  startTime: string | null;
  endTime: string | null;
  count: number;
  capacity: number;
  /** Termin liegt vor heute (nur relevant für abgelaufene Zeiträume). */
  isPast: boolean;
}

export interface GuestTrendPoint {
  label: string;
  value: number;
}

export interface DashboardGuest {
  id: number;
  /** Mitglieds-ID des Gasts (mg_nr) für das Detail-Modal. */
  memberId: number;
  memberNumber: string | number | null;
  vorname: string;
  name: string;
  info: string;
  start: string;
  isPast: boolean;
}

export interface DashboardAccount {
  id: number;
  name: string;
  iconKey: AccountIconKey;
  balance: number;
}

export interface CashflowMonth {
  label: string;
  ein: number;
  aus: number;
}

export interface DashboardDog {
  id: number;
  name: string;
  breed: string | null;
  ageYears: number | null;
  owner: string;
  /** Halter ist reiner Gast (nur Rolle `guest`). */
  ownerIsGuest: boolean;
  vaccination: DocStatus;
  vaccinationUntil: string | null;
  liability: DocStatus;
  insuranceName: string | null;
  optionalDocs: number;
}

export interface DogStats {
  total: number;
  openVaccination: number;
  overdueVaccination: number;
  missingLiability: number;
}

export interface RoleRow {
  role: RoleKey;
  count: number;
  pct: number;
}

export interface NewMemberRow {
  id: number;
  vorname: string;
  name: string;
  role: RoleKey;
  /** Backend-Rollenname ('board' | 'trainer' | …) für MemberAvatar. */
  roleName: string;
  memberNumber: string | number | null;
  since: string;
}

export interface OpenItems {
  consumption: number;
  consumptionMembers: number;
  contributionMembers: number;
  contributionYears: number;
}

export interface DashboardData {
  period: Period;
  periodLabel: string;
  previousLabel: string;
  stand: string;
  kpi: DashboardKpi;
  events: DashboardEvent[];
  guestTrend: GuestTrendPoint[];
  guests: DashboardGuest[];
  accounts: DashboardAccount[];
  cashflow: CashflowMonth[];
  dogs: DashboardDog[];
  dogStats: DogStats;
  roleRows: RoleRow[];
  newMembers: NewMemberRow[];
  open: OpenItems;
}

const EMPTY: DashboardData = {
  period: 'monat',
  periodLabel: 'Monat',
  previousLabel: '',
  stand: '',
  kpi: {
    activeMembers: 0,
    newMembers: 0,
    leftMembers: 0,
    growthPercent: 0,
    guestBookings: 0,
    guestBookingsPrevious: 0,
    newGuests: 0,
    events: 0,
    bookings: 0,
    capacity: 0,
    avgBookings: 0,
    utilization: 0,
    totalBalance: 0,
    accountCount: 0,
    reserves: 0,
    cashIn: 0,
    cashOut: 0,
  },
  events: [],
  guestTrend: [],
  guests: [],
  accounts: [],
  cashflow: [],
  dogs: [],
  dogStats: { total: 0, openVaccination: 0, overdueVaccination: 0, missingLiability: 0 },
  roleRows: [],
  newMembers: [],
  open: { consumption: 0, consumptionMembers: 0, contributionMembers: 0, contributionYears: 0 },
};

export function useVorstandsDashboard(enabled: boolean, period: Period) {
  const [data, setData] = useState<DashboardData>(EMPTY);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(`/api/dashboard/vorstand?period=${period}`);
      if (res.data?.success) {
        setData({ ...EMPTY, ...res.data });
      } else {
        setError('Die Dashboard-Daten konnten nicht geladen werden.');
      }
    } catch (e) {
      console.error('Vorstands-Dashboard: Laden fehlgeschlagen', e);
      setError('Die Dashboard-Daten konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [enabled, period]);

  useEffect(() => {
    load();
    // Kein Intervall: absichtlich kein Auto-Refresh.
  }, [load]);

  return { data, loading, error, refresh: load };
}

export default useVorstandsDashboard;
